import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigurationService } from '@core/services/configuration.service';

export const ISSUE_MODULES = ['Home', 'Projects', 'Audit', 'NC', 'CAPA', 'Inspections', 'Observations', 'Dashboards', 'Supply Chain Quality', 'Other'] as const;
export type IssueModule = typeof ISSUE_MODULES[number];
export interface IssueContext {
  route: string;
  projectId: string | null;
  recordId: string | null;
  module: IssueModule;
}
export interface IssueDraft extends IssueContext {
  requestId: string;
  title: string;
  description: string;
}
export interface IssueReceipt {
  id: string;
  notificationSent: boolean;
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MODULE_ROUTES: Record<string, IssueModule> = {
  home: 'Home', projects: 'Projects', audits: 'Audit', 'audit-enhanced': 'Audit',
  ncrs: 'NC', 'nc-enhanced': 'NC', 'nc-simplified': 'NC',
  capas: 'CAPA', 'capa-enhanced': 'CAPA', inspections: 'Inspections',
  'inspection-assignments': 'Inspections', 'inspection-packages': 'Inspections', 'inspection-reports': 'Inspections',
  observations: 'Observations', dashboard: 'Dashboards', dashboards: 'Dashboards',
  suppliers: 'Supply Chain Quality', 'supply-chain': 'Supply Chain Quality',
  'capa-simplified': 'CAPA', 'capas-simplified': 'CAPA', 'capas-enhanced': 'CAPA',
  'ncrs-simplified': 'NC', 'ncrs-enhanced': 'NC', 'audits-classic': 'Audit',
  'inspections-hub': 'Inspections', 'inspection-enhanced': 'Inspections', inspector: 'Inspections',
  supplier: 'Supply Chain Quality', scorecard: 'Supply Chain Quality', aml: 'Supply Chain Quality',
  'inspection-agencies': 'Supply Chain Quality', scars: 'CAPA',
};
const SAFE_SEGMENTS = new Set([...Object.keys(MODULE_ROUTES), 'portal', 'new', 'edit', 'view', 'detail', 'details', 'members', 'catalog', 'reports', 'assignments', 'packages', 'overview', 'kpis', 'quality', 'sqe', 'itps', 'other',
  'documents', 'training', 'risks', 'glossary', 'admin', 'switch-role', 'quality-events', 'ofi', 'search', 'activity', 'my-assignments',
  'coordinator', 'external-coordinator', 'users', 'settings', 'permissions']);

export function captureIssueContext(url: string, projectId: string | null): IssueContext {
  const segments = url.split(/[?#]/, 1)[0].split('/').filter(Boolean);
  const safe = segments.length <= 8 && segments.every(segment => SAFE_SEGMENTS.has(segment) || GUID.test(segment));
  const route = safe && segments.length ? `/${segments.join('/')}` : '/other';
  const module = safe ? segments.map(segment => MODULE_ROUTES[segment]).reverse().find(Boolean) ?? 'Other' : 'Other';
  const identifiers = safe ? segments.filter(segment => GUID.test(segment)) : [];
  const recordId = module === 'Projects' ? null : identifiers.at(-1) ?? null;
  return { route, projectId: projectId && GUID.test(projectId) ? projectId : null, recordId, module };
}

@Injectable({ providedIn: 'root' })
export class IssueReportService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigurationService);

  submit(draft: IssueDraft, files: readonly File[]) {
    const body = new FormData();
    body.append('requestId', draft.requestId);
    body.append('title', draft.title.trim());
    body.append('description', draft.description);
    body.append('module', draft.module);
    body.append('route', draft.route);
    if (draft.projectId) body.append('projectId', draft.projectId);
    if (draft.recordId) body.append('recordId', draft.recordId);
    files.forEach(file => body.append('files', file, file.name));
    return this.http.post<IssueReceipt>(`${this.config.apiBaseUrl}/issue-reports`, body);
  }
}